import type { ColDef } from "ag-grid-community";
import i18n from "i18next";
import moment from "moment";
import { userStatus } from "src/constants/users";
import k from "src/i18n/keys";
import type { PeopleRowData, UID } from "src/types";
import {
  DepartmentCellRenderer,
  GroupsCellRenderer,
  PhoneNumberCellRenderer,
  RoleCellRenderer,
  UserCellRenderer,
} from "./cellRenderers";
import DepartmentFilter from "./filters/DepartmentFilter";
import GroupFilter from "./filters/GroupFilter";
import RoleFilter from "./filters/RoleFilter";
import StatusFilter from "./filters/StatusFilter";

interface GetColumnsParams {
  onUserClick: (uid: UID) => void;
  roleTitles: Array<{ id: number; title: string; label: string }>;
}

export const getColumns = ({
  onUserClick,
  roleTitles,
}: GetColumnsParams): Array<ColDef<PeopleRowData>> => [
  {
    headerName: i18n.t(k.DISPLAY_NAME),
    field: "displayName",
    resizable: true,
    flex: 2.3,
    minWidth: 210,
    cellRenderer: UserCellRenderer,
    cellRendererParams: { onUserClick },
    valueGetter: (params) => params.data,
    getQuickFilterText: (params) => params.data?.displayName || "",
    sortable: true,
    comparator: (userA, userB) => {
      const nameA = userA?.displayName || userA?.email || "";
      const nameB = userB?.displayName || userB?.email || "";
      return nameA.localeCompare(nameB);
    },
    filter: false,
    pinned: "left",
  },
  {
    headerName: i18n.t(k.STATUS),
    field: "status",
    width: 120,
    valueGetter: (params) => {
      const user = params.data;
      if (!user) return userStatus.active;
      if (user.disabled) return userStatus.disabled;
      if (user.invitePending) return userStatus.pending;
      return userStatus.active;
    },
    sortable: true,
    filter: StatusFilter,
    filterValueGetter: (params) => params.data?.uid,
  },
  {
    headerName: i18n.t(k.EMAIL_ID),
    field: "email",
    flex: 2,
    minWidth: 150,
    resizable: true,
    sortable: true,
    filter: false,
  },
  {
    headerName: i18n.t(k.ROLE1),
    field: "orgRoleId",
    flex: 2,
    minWidth: 165,
    maxWidth: 200,
    cellRenderer: RoleCellRenderer,
    cellRendererParams: { roleTitles },
    sortable: true,
    comparator: (roleIdA, roleIdB) => {
      const toLabel = (roleId: unknown) => {
        const idNum = Number(roleId);
        const role = roleTitles.find((r) => r.id === idNum);
        return role?.label || "";
      };

      return toLabel(roleIdA)?.localeCompare(toLabel(roleIdB) || "");
    },
    filter: RoleFilter,
    filterValueGetter: (params) => params.data?.uid,
  },
  {
    headerName: i18n.t(k.DEPARTMENT),
    field: "department",
    flex: 1.5,
    minWidth: 120,
    cellRenderer: DepartmentCellRenderer,
    sortable: false,
    filter: DepartmentFilter,
    filterValueGetter: (params) => params.data?.uid,
  },
  {
    headerName: i18n.t(k.PHONE_NUMBER),
    field: "phoneNumber",
    flex: 1.5,
    minWidth: 120,
    cellRenderer: PhoneNumberCellRenderer,
    sortable: true,
    filter: false,
  },
  {
    headerName: i18n.t(k.GROUPS),
    field: "groups",
    flex: 2,
    minWidth: 230,
    cellRenderer: GroupsCellRenderer,
    sortable: false,
    filter: GroupFilter,
    filterValueGetter: (params) => params.data?.uid,
  },
  {
    headerName: i18n.t(k.LAST_LOGGED_IN),
    field: "lastLogin",
    flex: 1.8,
    minWidth: 180,
    maxWidth: 180,
    valueGetter: (params) => {
      const user = params.data;
      if (!user?.lastLogin) return null;
      const lastLogin: any = user.lastLogin;
      if (lastLogin?.toDate) {
        return lastLogin.toDate().getTime();
      }
      if (lastLogin?.valueOf) {
        return lastLogin.valueOf();
      }
      return null;
    },
    valueFormatter: (params) => {
      if (!params.value) return "";
      return moment(params.value).format(i18n.t(k.MMM_DD_YYYY_H_MM_A));
    },
    sortable: true,
    filter: false,
  },
  {
    headerName: i18n.t(k.DATE_INVITED),
    field: "inviteDate",
    flex: 1.8,
    minWidth: 180,
    maxWidth: 180,
    valueGetter: (params) => {
      const user = params.data;
      if (!user?.inviteDate) return null;
      const dateInvited: any = user.inviteDate;
      if (dateInvited?.toDate) {
        return dateInvited.toDate().getTime();
      }
      if (dateInvited?.valueOf) {
        return dateInvited.valueOf();
      }
      return null;
    },
    valueFormatter: (params) => {
      if (!params.value) return "";
      return moment(params.value).format(i18n.t(k.MMM_DD_YYYY_H_MM_A));
    },
    sortable: true,
    filter: false,
  },
  {
    headerName: i18n.t(k.DATE_JOINED),
    field: "joinDate",
    valueGetter: (params) => {
      const user = params.data;
      if (!user?.joinDate) return null;
      const dateJoined: any = user.joinDate;
      if (dateJoined?.toDate) {
        return dateJoined.toDate().getTime();
      }
      if (dateJoined?.valueOf) {
        return dateJoined.valueOf();
      }
      return null;
    },
    valueFormatter: (params) => {
      if (!params.value) return "";
      return moment(params.value).format(i18n.t(k.MMM_DD_YYYY_H_MM_A));
    },
    sortable: true,
    filter: false,
  },
];
